import { useAuth0 } from '@auth0/auth0-react'
import classNames from 'classnames'
import { Link } from 'gatsby'
import React, { useState, useRef, useContext } from 'react'

import { LocaleContext } from '../../locale/i18n'
import { ISlugLink, staticPagePaths } from '../../locale/pathing'
import { navLinks } from '../../logic/navLinks'
import { StaticPage } from '../../logic/staticPage'
import useOnClickOutside from '../../logic/useOnClickOutside'
import Button from '../Button'
import IconClose from '../icons/IconClose'
import IconMenu from '../icons/IconMenu'
import IconUser from '../icons/IconUser'
import MeasurLogo from '../MeasurLogo'

import { LocaleSelectorDesktop, LocaleSelectorMobile } from './LocaleSelector'
import LoggedInMenu from './LoggedInMenu'
import ShoppingCart from './ShoppingCart'
import SiteSearch from './SiteSearch'

import './TopNav.scss'

const NavBar = ({
  isMobileMenuOpen,
  localisedSlugs,
  setIsMobileMenuOpen,
}: {
  isMobileMenuOpen: boolean
  localisedSlugs: ISlugLink[]
  setIsMobileMenuOpen: (isOpen: boolean) => void
}) => {
  const [isSearchVisible, setIsSearchVisible] = useState(false)
  const { locale, t } = useContext(LocaleContext)

  // For useOutsideClick
  const ref = useRef<HTMLDivElement>(null)

  const { isAuthenticated } = useAuth0()

  useOnClickOutside(ref, () => {
    setIsMobileMenuOpen(false)
  })

  return (
    <div className="navbar__nav gutter gutter--center" ref={ref}>
      <Link to={staticPagePaths[StaticPage.INDEX][locale]}>
        <MeasurLogo isCollapsible />
      </Link>

      <SiteSearch
        setIsVisible={v => setIsSearchVisible(v)}
        isVisible={isSearchVisible}
      />

      <div
        className={classNames('navbar__links', {
          'navbar__links--hide': isSearchVisible,
        })}
      >
        {navLinks.map(page => (
          <Link
            key={page}
            to={staticPagePaths[page][locale]}
            className="navbar__link"
            activeClassName="active"
            partiallyActive
          >
            {t(`nav.${page}`)}
          </Link>
        ))}
        <LocaleSelectorMobile localisedSlugs={localisedSlugs} />
      </div>
      <ShoppingCart />
      <div className="navbar__separator" />
      {!isAuthenticated && (
        <a
          className="navbar__loginLink"
          href={staticPagePaths[StaticPage.PORTAL][locale]}
          aria-label={t('portal.loginButton')}
          data-test-id="login-button"
        >
          <IconUser />
          <span>{t('portal.loginButtonShort')}</span>
        </a>
      )}
      <LocaleSelectorDesktop localisedSlugs={localisedSlugs} />

      <Button
        aria-label="Menu"
        className="navbar__icon nav:hidden!"
        onClick={event => {
          event.stopPropagation()
          setIsMobileMenuOpen(!isMobileMenuOpen)
        }}
        variant="plain"
      >
        {isMobileMenuOpen ? <IconClose /> : <IconMenu />}
      </Button>
    </div>
  )
}

const TopNav = ({ localisedSlugs }: { localisedSlugs: ISlugLink[] }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const ref = useRef<HTMLDivElement>(null)
  const { isAuthenticated } = useAuth0()

  return (
    <nav className="topNav h-18">
      <div
        className={classNames('topNav__stickyContent', {
          'topNav__stickyContent--open': isMobileMenuOpen,
        })}
        ref={ref}
      >
        <NavBar
          isMobileMenuOpen={isMobileMenuOpen}
          localisedSlugs={localisedSlugs}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />
        {isAuthenticated && <LoggedInMenu />}
      </div>
    </nav>
  )
}

export default TopNav
